import { useState, useEffect } from 'react';
import { adminApi } from '../../api';
import api from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Bell, Send, Users, Mail, Loader2, Megaphone, Clock, Trash2 } from 'lucide-react';

const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { 
  year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
});

export default function AdminNotifications() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    message: '',
    send_email: false,
    show_popup: true
  });

  useEffect(() => { fetchBroadcasts(); }, []);

  const fetchBroadcasts = async () => {
    try {
      const response = await api.get('/admin/broadcasts');
      setBroadcasts(response.data);
    } catch (error) { console.error('Failed to fetch broadcasts:', error); }
    finally { setIsLoading(false); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSending(true);
    try {
      const response = await api.post('/admin/broadcast', form);
      toast.success(response.data.message);
      setShowDialog(false);
      setForm({ title: '', message: '', send_email: false, show_popup: true });
      fetchBroadcasts();
    } catch (error) {
      toast.error('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteBroadcast = async (broadcastId) => {
    if (!window.confirm('Are you sure you want to delete this broadcast?')) return;
    
    setDeleting(true);
    try {
      await adminApi.deleteBroadcast(broadcastId);
      toast.success('Broadcast deleted');
      fetchBroadcasts();
    } catch (error) {
      toast.error('Failed to delete broadcast');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAllBroadcasts = async () => {
    if (!window.confirm('Are you sure you want to delete ALL broadcasts? This cannot be undone.')) return;
    
    setDeleting(true);
    try {
      await adminApi.deleteAllBroadcasts();
      toast.success('All broadcasts deleted');
      fetchBroadcasts();
    } catch (error) {
      toast.error('Failed to delete broadcasts');
    } finally {
      setDeleting(false);
    }
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">Notifications & Broadcasts</h1>
          <p className="text-zinc-500">Send announcements to all users</p>
        </div>
        
        <div className="flex items-center gap-2">
          {broadcasts.length > 0 && (
            <Button
              variant="outline"
              onClick={handleDeleteAllBroadcasts}
              disabled={deleting}
              className="border-red-500/30 text-red-500 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />Delete All
            </Button>
          )}
          
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#FFD700] text-black hover:bg-[#E5C100] font-bold">
                <Megaphone className="w-4 h-4 mr-2" />New Broadcast
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-2 border-[#FFD700]/30 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white font-bold text-xl flex items-center gap-2">
                  <Bell className="w-5 h-5 text-[#FFD700]" />Send Broadcast Message
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSend} className="space-y-4">
                <div>
                  <Label className="text-[#FFD700] font-bold uppercase text-xs">Title</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm({...form, title: e.target.value})}
                    placeholder="e.g., New Products Available!"
                    className="bg-[#121212] border-2 border-white/20 text-white font-semibold focus:border-[#FFD700]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-[#FFD700] font-bold uppercase text-xs">Message</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({...form, message: e.target.value})}
                    placeholder="Write your announcement message here..."
                    className="bg-[#121212] border-2 border-white/20 text-white focus:border-[#FFD700] h-32"
                    required
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-[#121212] rounded-lg border border-white/10">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#FFD700]" />
                    <div>
                      <p className="text-white font-semibold">Also send via Email</p>
                      <p className="text-xs text-zinc-500">Send to all user email addresses</p>
                    </div>
                  </div>
                  <Switch
                    checked={form.send_email}
                    onCheckedChange={(checked) => setForm({...form, send_email: checked})}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-[#121212] rounded-lg border border-white/10">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-[#FFD700]" />
                    <div>
                      <p className="text-white font-semibold">Show as Popup</p>
                      <p className="text-xs text-zinc-500">Display popup notification to users</p>
                    </div>
                  </div>
                  <Switch
                    checked={form.show_popup}
                    onCheckedChange={(checked) => setForm({...form, show_popup: checked})}
                  />
                </div>
                <Button type="submit" disabled={sending} className="w-full bg-[#FFD700] text-black font-bold hover:bg-[#E5C100]">
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Broadcast</>}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Broadcast History */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-white">Broadcast History</h2>
        {broadcasts.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-8 text-center">
              <Megaphone className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
              <p className="text-zinc-400">No broadcasts sent yet</p>
            </CardContent>
          </Card>
        ) : (
          broadcasts.map((broadcast) => (
            <Card key={broadcast.id} className="bg-[#0A0A0A] border-2 border-[#FFD700]/20">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-10 h-10 bg-[#FFD700]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-[#FFD700]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-white">{broadcast.title}</p>
                      {broadcast.message && (
                        <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{broadcast.message}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-sm text-zinc-500 flex-wrap">
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{broadcast.recipients_count} users</span>
                        {broadcast.emails_sent > 0 && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{broadcast.emails_sent} emails</span>}
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(broadcast.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-500/20 text-green-500 font-bold">Sent</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteBroadcast(broadcast.id)}
                      disabled={deleting}
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
